import EditClientDialog from './EditClientDialog';
import React, { useEffect, useMemo, useState } from 'react';
import EntityDetailDialog from '../../../shared/components/EntityDetailDialog';
import RowActions from '../../../shared/components/patterns/RowActions';
import ClientNotesPanel from '@/modules/clients/components/ClientNotesPanel';
import { CLIENT_TYPE_LABELS } from '@/modules/clients/constants/client.constants';
import { getCanonicalClientContacts } from '../utils/clientContacts.helpers';
import ClientCustomFieldsSection from './ClientCustomFieldsSection';
import '../styles/clients-detail.css';

function money(amount) {
  if (amount === null || amount === undefined || amount === '') return '—';
  return new Intl.NumberFormat('it-IT', { style: 'currency', currency: 'EUR' }).format(amount);
}

function renderFinancialValue(amount, { loading = false, unavailable = false } = {}) {
  if (loading) return '...';
  if (unavailable) return '—';
  return money(amount);
}

function renderActivityCount(count, { loading = false, status = 'idle' } = {}) {
  if (loading) return '...';
  if (status === 'unavailable' || status === 'auth_required') return '—';
  if (count === null || count === undefined) return '—';
  return String(count);
}

function getSectionMessage(status, emptyMessage) {
  if (status === 'auth_required') return 'Sessione non autorizzata per questa sezione.';
  if (status === 'unavailable') return 'Dati non disponibili dal backend.';
  if (status === 'empty') return emptyMessage;
  return '';
}

function normalizeComparable(value) {
  return String(value || '').trim().toLowerCase();
}

function getStaffDisplayName(member) {
  return (
    member?.fullName ||
    member?.name ||
    member?.displayName ||
    [member?.firstName, member?.lastName].filter(Boolean).join(' ') ||
    ''
  ).trim();
}

export function resolveReferredByLabel(referredBy, staff = []) {
  const value = String(referredBy || '').trim();
  if (!value) return '—';

  const normalizedValue = normalizeComparable(value);
  const matchedStaff = (staff || []).find((member) => {
    const displayName = getStaffDisplayName(member);
    return [
      member?.id,
      member?.linkedWorkspaceUserId,
      member?.workspaceUserId,
      member?.userId,
      member?.email,
      member?.workEmail,
      displayName,
    ].some((candidate) => normalizeComparable(candidate) === normalizedValue);
  });

  return getStaffDisplayName(matchedStaff) || value;
}

const selectableTextClass = 'select-text';

function DetailSection({ title, children }) {
  return (
    <section className="client-detail__section" aria-label={title}>
      <h3 className="client-detail__section-title">{title}</h3>
      {children}
    </section>
  );
}

function DetailField({ label, value, wide = false }) {
  return (
    <div className={`client-detail__field${wide ? ' client-detail__field--wide' : ''}`}>
      <dt>{label}</dt>
      <dd className={selectableTextClass}>{value || '—'}</dd>
    </div>
  );
}

function ClientDetailDrawer({
  open,
  client,
  onClose,
  onEdit,
  onSave,
  onOpenDeals,
  onOpenAssignments,
  onOpenInProgressDeals,
  onOpenCompletedDeals,
  onOpenAccounting,
  onOpenDocuments,
  onArchive,
  financials,
  financialsLoading = false,
  financialsUnavailable = false,
  activity,
  activityLoading = false,
  staff = [],
}) {
  const [editing, setEditing] = useState(false);
  useEffect(() => { setEditing(false); }, [client?.id, open]);
  const safeFinancials = useMemo(() => financials || {}, [financials]);
  const dealsActivity = activity?.deals || { status: 'idle', counts: {} };
  const assignmentsActivity = activity?.assignments || { status: 'idle', counts: {}, assignments: [] };
  const accountingActivity = activity?.accounting || { status: 'idle', counts: {} };
  const documentsActivity = activity?.documents || { status: 'idle', counts: {}, documents: [] };
  const clientInitial = (client?.name || client?.company || 'C').trim().charAt(0).toUpperCase();
  const referredByLabel = resolveReferredByLabel(client?.referredBy, staff);
  const canonicalContacts = useMemo(() => getCanonicalClientContacts(client), [client]);
  const badges = client?.tipoCliente
    ? [{ label: CLIENT_TYPE_LABELS[client.tipoCliente] || client.tipoCliente, tone: 'active' }]
    : [];

  if (editing && client) return <EditClientDialog open={open} client={client} staff={staff} onSave={onSave} onClose={() => setEditing(false)} />;

  return (
    <EntityDetailDialog
      isOpen={open}
      onClose={onClose}
      title={client?.name || 'Dettaglio cliente'}
      subtitle={client?.company || client?.businessName || 'Scheda cliente'}
      avatarLabel={clientInitial}
      badges={badges}
      editActions={onSave && client ? (
        <RowActions actions={[{ id: 'edit', iconKey: 'actions.edit', label: 'Modifica cliente', onClick: () => setEditing(true) }]} />
      ) : onEdit && client ? (
        <RowActions actions={[{
          id: 'edit',
          iconKey: 'actions.edit',
          label: 'Modifica cliente',
          onClick: () => onEdit(client),
        }]} />
      ) : null}
      className="clients-detail-dialog"
    >
      {client && (
        <div className="client-detail__layout">
          <DetailSection title="Informazioni aziendali">
            <dl className="client-detail__field-grid">
              <DetailField label="Azienda" value={client.company} />
              <DetailField label="Nome referente" value={client.name} />
              <DetailField label="Ragione sociale" value={client.businessName || client.company} />
              <DetailField label="Tipo cliente" value={CLIENT_TYPE_LABELS[client.tipoCliente] || client.tipoCliente} />
              <DetailField label="Codice fiscale" value={client.codiceFiscale} />
              <DetailField label="Partita IVA" value={client.vatNumber} />
              <DetailField label="Numero REA" value={client.numeroREA} />
              <DetailField label="Forma giuridica" value={client.formaGiuridica} />
              <DetailField label="Settore" value={client.sector} />
              <DetailField label="Riferito da" value={referredByLabel} />
              <DetailField label="Indirizzo sede legale" value={client.indirizzoSedeLegale} wide />
            </dl>
          </DetailSection>

          <DetailSection title="Contatti">
            <div className="client-detail__contacts">
              <div>
                <h4>Contatti personali</h4>
                <dl className="client-detail__contact-fields">
                  <DetailField label="Email" value={canonicalContacts.contattiPersonali.email} />
                  <DetailField label="Telefono" value={canonicalContacts.contattiPersonali.phone} />
                </dl>
              </div>
              <div>
                <h4>Contatti aziendali</h4>
                <dl className="client-detail__contact-fields">
                  <DetailField label="Email" value={canonicalContacts.contattiAziendali.email} />
                  <DetailField label="Telefono" value={canonicalContacts.contattiAziendali.phone} />
                </dl>
              </div>
            </div>
          </DetailSection>

          <DetailSection title="Overview cliente">
            <div className="client-detail__metrics">
              <div className="client-detail__metric">
                <span>Task collegati</span>
                <strong>
                  {renderActivityCount(assignmentsActivity.counts?.total, {
                    loading: activityLoading,
                    status: assignmentsActivity.status,
                  })}
                </strong>
                <p>
                  {renderActivityCount(assignmentsActivity.counts?.pending, {
                    loading: activityLoading,
                    status: assignmentsActivity.status,
                  })} da accettare · {renderActivityCount(assignmentsActivity.counts?.inProgress, {
                    loading: activityLoading,
                    status: assignmentsActivity.status,
                  })} in corso · {renderActivityCount(assignmentsActivity.counts?.completed, {
                    loading: activityLoading,
                    status: assignmentsActivity.status,
                  })} completati
                </p>
                {getSectionMessage(assignmentsActivity.status, 'Nessun task collegato.') ? (
                  <p className="entity-detail-dialog__notice">
                    {getSectionMessage(assignmentsActivity.status, 'Nessun task collegato.')}
                  </p>
                ) : null}
              </div>
              <div className="client-detail__metric">
                <span>Affari collegati</span>
                <strong>
                  {renderActivityCount(dealsActivity.counts?.total, {
                    loading: activityLoading,
                    status: dealsActivity.status,
                  })}
                </strong>
                <p>
                  {renderActivityCount(dealsActivity.counts?.proposals, {
                    loading: activityLoading,
                    status: dealsActivity.status,
                  })} proposte · {renderActivityCount(dealsActivity.counts?.inProgress, {
                    loading: activityLoading,
                    status: dealsActivity.status,
                  })} in corso · {renderActivityCount(dealsActivity.counts?.completed, {
                    loading: activityLoading,
                    status: dealsActivity.status,
                  })} completati
                </p>
              </div>
              <div className="client-detail__metric">
                <span>Valore totale</span>
                <strong>
                  {renderFinancialValue(safeFinancials.total, {
                    loading: financialsLoading,
                    unavailable: financialsUnavailable,
                  })}
                </strong>
                <p>
                  {renderFinancialValue(safeFinancials.pending, {
                    loading: financialsLoading,
                    unavailable: financialsUnavailable,
                  })} da incassare
                </p>
              </div>
              <div className="client-detail__metric">
                <span>Documenti / Contabilità</span>
                <strong>
                  {renderActivityCount(documentsActivity.counts?.total, {
                    loading: activityLoading,
                    status: documentsActivity.status,
                  })}
                </strong>
                <p>
                  {renderActivityCount(accountingActivity.counts?.invoices, {
                    loading: activityLoading,
                    status: accountingActivity.status,
                  })} fatture
                  {' '}·{' '}
                  {renderActivityCount(accountingActivity.counts?.openInvoices, {
                    loading: activityLoading,
                    status: accountingActivity.status,
                  })} aperte
                </p>
                {getSectionMessage(documentsActivity.status, 'Nessun documento collegato.') ? (
                  <p className="entity-detail-dialog__notice">
                    {getSectionMessage(documentsActivity.status, 'Nessun documento collegato.')}
                  </p>
                ) : null}
              </div>
            </div>
          </DetailSection>

          <DetailSection title="Descrizione interna">
            <p className="client-detail__description select-text">{client.notes || '—'}</p>
          </DetailSection>

          <ClientCustomFieldsSection readOnly formKey="clients.client.create" values={client.customFields || {}} />
          <ClientNotesPanel clientId={client.id} open={open && Boolean(client.id)} staff={staff} />
        </div>
      )}
    </EntityDetailDialog>
  );
}

export default ClientDetailDrawer;
